import type { Store } from "vuex";
import type { State } from "../state";
import type { AppNotification, MainState } from "./state";
import type { IUserProfile } from "@/interfaces";
import type { GetterTree } from "vuex";

export const getters: GetterTree<MainState, State> = {
  hasAdminAccess: (state): boolean => {
    return !!(
      state.userProfile &&
      state.userProfile.is_superuser &&
      state.userProfile.is_active
    );
  },

  canAcknowledge: (state): boolean => {
    return !!(
      state.userProfile &&
      state.userProfile.can_acknowledge_activities &&
      state.userProfile.is_active
    );
  },

  loginError: (state): boolean => state.logInError,
  dashboardShowDrawer: (state): boolean => state.dashboardShowDrawer,
  dashboardMiniDrawer: (state): boolean => state.dashboardMiniDrawer,

  userProfile: (state): IUserProfile | null => state.userProfile,

  token: (state): string => state.token,

  isLoggedIn: (state): boolean | null => state.isLoggedIn,

  firstNotification: (state): AppNotification | false =>
    state.notifications.length > 0 && state.notifications[0],

  msalConfig: (state): { clientId: string; authority: string } =>
    state.msalConfig,

  ssoEnabled: (state): boolean =>
    state.msalConfig.clientId !== "" && state.msalConfig.authority !== "",

  ssoLoggedIn: (state): boolean => state.ssoLoggedIn,

  tokenExpiry: (state): number => {
    if (state.token) {
      const token = state.token.split(".")[1];
      const decodedToken = JSON.parse(atob(token));
      return decodedToken.exp;
    }
    return 0;
  },

  mapBoxToken: (state): string => state.mapBoxToken,
  mapboxEnabled: (state): boolean => !!state.mapBoxToken,
};

// ---- Compatibility "read*" helpers (keeps your existing call sites) ----
// Because mainModule is namespaced: true, getters live at store.getters["main/<getterName>"]

const NS = "main/";

export const readDashboardMiniDrawer = (store: Store<State>) =>
  store.getters[`${NS}dashboardMiniDrawer`] as boolean;

export const readDashboardShowDrawer = (store: Store<State>) =>
  store.getters[`${NS}dashboardShowDrawer`] as boolean;

export const readHasAdminAccess = (store: Store<State>) =>
  store.getters[`${NS}hasAdminAccess`] as boolean;

export const readCanAcknowledge = (store: Store<State>) =>
  store.getters[`${NS}canAcknowledge`] as boolean;

export const readIsLoggedIn = (store: Store<State>) =>
  store.getters[`${NS}isLoggedIn`] as boolean | null;

export const readLoginError = (store: Store<State>) =>
  store.getters[`${NS}loginError`] as boolean;

export const readToken = (store: Store<State>) =>
  store.getters[`${NS}token`] as string;

export const readUserProfile = (store: Store<State>) =>
  store.getters[`${NS}userProfile`] as IUserProfile | null;

export const readFirstNotification = (store: Store<State>) =>
  store.getters[`${NS}firstNotification`] as AppNotification | false;

export function readMsalConfig(source: Store<State>): {
  clientId: string;
  authority: string;
};
export function readMsalConfig(source: MainState): {
  clientId: string;
  authority: string;
};
export function readMsalConfig(source: Store<State> | MainState): {
  clientId: string;
  authority: string;
} {
  // If it's a Store, read from getters (main module is namespaced)
  if ("getters" in source) {
    return source.getters[`${NS}msalConfig`] as {
      clientId: string;
      authority: string;
    };
  }

  // Otherwise it's the main module state directly
  return source.msalConfig;
}

export const readSSOEnabled = (store: Store<State>) =>
  store.getters[`${NS}ssoEnabled`] as boolean;

export const readSSOLoggedIn = (store: Store<State>) =>
  store.getters[`${NS}ssoLoggedIn`] as boolean;

export const readTokenExpiry = (store: Store<State>) =>
  store.getters[`${NS}tokenExpiry`] as number;

export const readMapBoxToken = (store: Store<State>) =>
  store.getters[`${NS}mapBoxToken`] as string;
export const readMapboxEnabled = (store: Store<State>) =>
  store.getters[`${NS}mapboxEnabled`] as boolean;
